import classNames from 'classnames'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getIcon } from '../../../../common/components/icons/utils'

import shared from '../../../../common/styles/shared.module.css'
import styles from './Card.module.css'
import { useUser } from '../../../../contexts/userContext'
import { preventUserCreateGame } from '../../../../util/game'
import { SmallModal } from '../../../../common/components/Modal/SmallModal'
import { Button } from '../../../../common/components/button/Button'
import i18n from '../../../../i18n'
import { useParams } from 'react-router-dom'
import { getClientInfo } from '../../../../util/browserSpecifics'

type CreateGameCardProps = {
  onClick: () => void
  onSaigeClick?: () => void
}

export const SAIGE_BUTTON_ID = 'SAIGE_BUTTON_ID'

export const CreateGameCard: React.FC<CreateGameCardProps> = ({ onClick, onSaigeClick }) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { gameId: gameIdParam } = useParams()
  const [showSaigeDisabledModal, setShowSaigeDisabledModal] = useState(false)
  const noSaigeRoundsLeft = (user?.engineRounds ?? 0) < 1 || !user?.hasSaigeAccess || preventUserCreateGame(user)
  const saigeNeverActivated = !user?.enginePin || user?.enginePin.length < 1

  const handleSaigeNoAccessClick = useCallback(() => {
    setShowSaigeDisabledModal(true)
  }, [])

  const handleHideSaigeDisabledModal = useCallback(() => {
    setShowSaigeDisabledModal(false)
  }, [])

  const handleContactSupport = useCallback(() => {
    if (!window.Beacon) {
      // Beacon not available; for now just log and close menu
      console.warn('HelpScout Beacon is not available')
      return
    }

    const clientInfo = getClientInfo()

    const identifyData: Record<string, any> = {}

    if (user?.contactName) identifyData.name = user.contactName
    if (user?.email) identifyData.email = user.email
    if (user?.activeBusiness?.id) identifyData['business-id'] = user.activeBusiness.id
    if (user?.activeBusiness?.industry) identifyData.industry = user.activeBusiness.industry
    if (gameIdParam) identifyData['game-id'] = gameIdParam
    if (user?.activeBusiness?.name) identifyData.company = user.activeBusiness.name

    // Always include client info if available
    identifyData['browser-name'] = clientInfo.browser
    identifyData['device-type'] = clientInfo.deviceType
    identifyData.platform = clientInfo.platform
    identifyData['user-agent'] = clientInfo.userAgent

    window.Beacon('identify', identifyData)

    handleHideSaigeDisabledModal()
    window.Beacon('open')
  }, [
    user?.contactName,
    user?.email,
    user?.activeBusiness.id,
    user?.activeBusiness.industry,
    user?.activeBusiness.name,
    gameIdParam,
    handleHideSaigeDisabledModal,
  ])

  const handleOpenLink = useCallback(() => {
    if (i18n.language === 'fi') {
      window.open('https://seppo.io/fi/tekoalyominaisuudet/', '_blank')
    } else if (i18n.language === 'pt') {
      window.open('https://seppo.io/pt/gamificacao-ia/', '_blank')
    } else {
      window.open('https://seppo.io/ai-game-engine/', '_blank')
    }
  }, [])

  return (
    <>
      <div className={styles.splitCreateNew}>
        <button
          type={'button'}
          className={classNames(styles.container, styles.outlined, shared.mAxisC, shared.sAxisC, styles.splitContainer)}
          style={{ width: '100%' }}
          onClick={onClick}
        >
          <span className={styles.splitIconWrapper}>{getIcon('plus')}</span>
          <p className={styles.text}>{t('dashboard.create_button', 'New game')}</p>
        </button>
        <button
          id={SAIGE_BUTTON_ID}
          type={'button'}
          className={classNames(
            styles.container,
            styles.secondaryOutlined,
            shared.mAxisC,
            shared.sAxisC,
            styles.splitContainer,
            noSaigeRoundsLeft && !saigeNeverActivated && styles.saigeDisabled,
          )}
          style={{ width: '100%' }}
          onClick={noSaigeRoundsLeft || saigeNeverActivated ? handleSaigeNoAccessClick : onSaigeClick}
        >
          <span className={classNames(styles.splitIconWrapper, styles.seppoAiIcon)}>{getIcon('seppoAiIcon')}</span>
          <p className={classNames(styles.secondaryText, styles.saigeButtonText)}>
            {noSaigeRoundsLeft || saigeNeverActivated
              ? saigeNeverActivated ? t('dashboard.ai_not_activated', 'Get Started with Saige') : t('dashboard.ai_no_rounds', 'Out of Saige tokens')
              : t('dashboard.ai_game_button', 'New with Saige')}
          </p>
        </button>
      </div>
      {showSaigeDisabledModal && (
        <SmallModal
          onClose={handleHideSaigeDisabledModal}
          onSubmit={handleHideSaigeDisabledModal}
          headerText={t('dashboard.saige_disabled_modal.title', 'Save time, create games like a pro!')}
          iconName='seppoAiIcon'
          hideCancelButton={true}
          sendButtonText={t('common.close', 'Close')}
        >
          <div className={styles.saigeDisabledModalBodyText}>
            {t(
              'dashboard.saige_disabled_modal.body',
              "It looks like SAIGE isn’t activated for your account yet, or you've run out of credits. To get started, simply click the button below to contact our customer support team. They’ll help you activate SAIGE or purchase more credits to continue creating your AI-powered games!",
            )}
            <br />
            <br />
            <div className={styles.saigeDisabledModalButtonRow}>
              <Button variant='outline-tiny' onClick={handleContactSupport}>
                <span className={styles.splitIconWrapper}>{getIcon('help')}</span>
                {t('dashboard.saige_disabled_modal.contact_support', 'Contact support')}
              </Button>
              <Button variant='outline-tiny' onClick={handleOpenLink}>
                <span className={styles.splitIconWrapper}>{getIcon('open')}</span>
                {t('dashboard.saige_disabled_modal.read_more', 'Read more')}
              </Button>
            </div>
          </div>
        </SmallModal>
      )}
    </>
  )
}
